import type { NavigationGuard } from 'vue-router';
import { exchangeOidcAuthCode, exchangeSamlAuthCode, getUserProfile } from '@/api/auth';
import { cancelOidcRefresh, scheduleOidcRefresh } from '@/composables/useOidcTokenRefresh';
import { useAppConfigStore } from '@/stores/appConfig';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { ENVIRONMENT, OIDC_SILENT_PARENT_ORIGIN, PEQUITY_API_URL } from '@/types';
import {
  clearIsOidcSession,
  clearTokens,
  consumeOidcPostLoginRedirect,
  getCorrelationId,
  getUrlBase,
  setAccessToken,
  setIsOidcSession,
  setOidcPostLoginRedirect,
  setRefreshToken,
} from '@/utils/helpers';

/**
 * This guard redirects the user to the SSO login page if the company is using SSO.
 */
export const redirectToSsoLoginIfUsingSso: NavigationGuard = async (to, from) => {
  if (to.query.error) {
    return { name: 'oidc-error', query: { done: '1' } };
  }

  const appConfigStore = useAppConfigStore();

  // OIDC (AIM) SSO — global setting
  if (appConfigStore.shouldRedirectToOidc) {
    if (from.name === 'oidc-login') {
      return;
    }

    const redirect = to.query.redirect;
    if (redirect && typeof redirect === 'string') {
      setOidcPostLoginRedirect(redirect);
    }

    window.location.href =
      ENVIRONMENT === 'local' ? PEQUITY_API_URL + '/oidc/authorize/' : getUrlBase() + '/api/oidc/authorize/';
    return false;
  }

  // SAML SSO — per-tenant setting (only when on a tenant subdomain)
  if (!appConfigStore.showTenantFinder) {
    return;
  }

  const companyStore = useCompanyStore();

  if (!companyStore.company?.id) {
    await companyStore.fetchCompanies();
  }

  if (companyStore.isUsingSSO) {
    const authStore = useAuthStore();
    await authStore.setUserProfileAndLoggedInState();

    if (authStore.loggedIn) {
      return { name: 'home' };
    }

    window.location.href = getUrlBase() + '/api/saml2/login';
    return false;
  }
};

/**
 * This guard handles the SAML login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleSamlLoginRedirect: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  clearTokens();

  const { data, error: exchangeError } = await exchangeSamlAuthCode(code);

  if (exchangeError || !data?.access_token || !data?.refresh_token) {
    return next({ name: 'welcome-back' });
  }

  clearIsOidcSession();
  cancelOidcRefresh();
  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);

  const { error } = await getUserProfile();

  if (error) {
    return next({ name: 'welcome-back' });
  } else {
    return next({ name: 'home' });
  }
};

/**
 * This guard handles the Lyric → Pequity OIDC SSO login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleOidcLoginRedirect: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  const { data, error: exchangeError } = await exchangeOidcAuthCode(code);

  if (exchangeError || !data?.access_token) {
    return next({ name: 'oidc-error' });
  }

  setAccessToken(data.access_token);
  setIsOidcSession();

  const authStore = useAuthStore();
  await authStore.setUserProfileAndLoggedInState();

  if (authStore.loggedIn) {
    scheduleOidcRefresh();
    const redirectPath = consumeOidcPostLoginRedirect();
    return redirectPath ? next(redirectPath) : next({ name: 'home' });
  } else {
    clearTokens();
    consumeOidcPostLoginRedirect();
    return next({ name: 'oidc-error' });
  }
};

/**
 * This guard handles silent OIDC auth performed inside a hidden iframe embedded
 * by the MFE host (lyric.adp.com). Instead of establishing a frontendv3 session,
 * it relays the outcome to the parent window via postMessage and lets the parent
 * own token storage and refresh. It must NOT write tokens/cookies or start refresh
 * loops here.
 */
export const handleOidcSilentRedirect: NavigationGuard = async (to) => {
  // Runs inside the hidden iframe: console is the only window into this side, so
  // log every branch to make a failed silent-auth attempt diagnosable.
  const logPrefix = '[oidc-silent-iframe]';
  const postToParent = (message: Record<string, unknown>) => {
    // Log only the type — the success payload carries the JWT, which must not be logged.
    console.info(logPrefix, 'posting to parent', OIDC_SILENT_PARENT_ORIGIN, message.type);
    window.parent.postMessage(message, OIDC_SILENT_PARENT_ORIGIN);
  };

  if (to.query.error && typeof to.query.error === 'string') {
    console.warn(logPrefix, 'AIM returned error on callback:', to.query.error);
    postToParent({ type: 'PEQUITY_AUTH_REQUIRED', error: to.query.error });
    return false;
  }

  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    console.error(logPrefix, 'no auth code present in callback redirect');
    postToParent({ type: 'PEQUITY_AUTH_ERROR', reason: 'missing_code' });
    return false;
  }

  const { data, error } = await exchangeOidcAuthCode(code);

  if (error || !data?.access_token) {
    console.error(logPrefix, 'token exchange failed:', error);
    postToParent({ type: 'PEQUITY_AUTH_ERROR', reason: 'exchange_failed' });
    return false;
  }

  console.info(logPrefix, 'token exchange succeeded — relaying JWT to parent');
  postToParent({ type: 'PEQUITY_AUTH_SUCCESS', accessToken: data.access_token });
  return false;
};

export const goHomeIfNoCorrelationId: NavigationGuard = async (to, from, next) => {
  if (getCorrelationId() === null) {
    return next({ name: 'home' });
  }
  return next();
};

export const redirectIfNoTenantFinder: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showTenantFinder) {
    return { name: 'welcome-back' };
  }
};

export const redirectIfNoPublicLogin: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showPublicLogin) {
    return { name: 'welcome-back' };
  }
};

export const redirectToPublicLoginIfNeeded: NavigationGuard = async (to) => {
  const appConfigStore = useAppConfigStore();

  if (appConfigStore.showPublicLogin) {
    return { name: 'public-login', query: to.query };
  }
};

export const redirectToTenantFinderIfNoSubdomain: NavigationGuard = async () => {
  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.showTenantFinder) {
    return;
  }

  const baseHostname = new URL(appConfigStore.frontendBaseUrl).hostname;

  if (window.location.hostname === baseHostname) {
    return { name: 'universal-login' };
  }
};
